/**
 * presenceOrchestrator — the pure budget/cadence decider for Djay's voice.
 *
 * App-Feel 2027 §3B / §5.2 seam #1. Framework-free so it is trivially testable
 * and reusable (the §11.3 global interruption-orchestrator, W12, can later wrap
 * the same primitive). It holds NO timers and NO React state — it is a plain
 * state machine: feed it the current state + a request, get a decision back.
 *
 * Anti-bandaid alignment (ARCHITECTURAL_PEER_REVIEW_PLAN_2026 Deel IV):
 *   #2 the verdict is an explicit union, not a boolean;
 *   #3 every drop has a NAMED, inspectable reason (no silent `return`);
 *   #4 the budget/cadence lives in code, observable, not in scattered effects.
 */

import { PRESENCE_POLICY, priorityOf, type PresenceRequest, type ProactiveKind } from './presenceTypes';

/** Mutable session-scoped budget state. Reset on account switch. */
export interface PresenceState {
  /** Proactive utterances granted this session. */
  spokenCount: number;
  /** Timestamp of the last GRANTED proactive utterance (0 = none yet). */
  lastSpokeAt: number;
}
export function createPresenceState(): PresenceState {
  return {
    spokenCount: 0,
    lastSpokeAt: 0
  };
}

/** Why a proactive request was allowed or denied — named, never silent (#3). */
export type PresenceVerdict = {
  allow: true;
} | {
  allow: false;
  reason: 'budget-exhausted' | 'within-cooldown' | 'suppressed-by-surface';
};
export interface PresenceContext {
  /**
   * True when the current surface already shows its own primary Djay/hero
   * (e.g. the HomePage PageHero greets). The floating voice stays silent so a
   * screen has ONE primary Djay voice at a time (§3B presence-policy, broadened
   * beyond the old home-only check). Visibility/tap are unaffected.
   */
  surfaceHasOwnDjay: boolean;
}

/**
 * Decide whether a proactive request may speak NOW. Pure: does not mutate
 * state — call `commitSpoke` separately once the caller acts on an `allow`.
 */
export function evaluatePresence(state: PresenceState, request: PresenceRequest, ctx: PresenceContext): PresenceVerdict {
  if (ctx.surfaceHasOwnDjay) {
    return {
      allow: false,
      reason: 'suppressed-by-surface'
    };
  }
  if (state.spokenCount >= PRESENCE_POLICY.MAX_PER_SESSION) {
    return {
      allow: false,
      reason: 'budget-exhausted'
    };
  }
  const gap = request.at - state.lastSpokeAt;
  if (state.lastSpokeAt !== 0 && gap < PRESENCE_POLICY.MIN_GAP_MS) {
    return {
      allow: false,
      reason: 'within-cooldown'
    };
  }
  return {
    allow: true
  };
}

/** Record that a proactive utterance was granted. Returns the next state. */
export function commitSpoke(state: PresenceState, at: number): PresenceState {
  return {
    spokenCount: state.spokenCount + 1,
    lastSpokeAt: at
  };
}

/**
 * When several proactive kinds want to speak at (about) the same moment, pick
 * the highest-priority one (§3B: errorTip > viewGreet > welcome > timeEgg >
 * idle). Returns the winner, or null for an empty set.
 */
export function pickHighestPriority(kinds: readonly ProactiveKind[]): ProactiveKind | null {
  if (kinds.length === 0) return null;
  return kinds.reduce((best, k) => priorityOf(k) < priorityOf(best) ? k : best);
}